import { useCallback, useContext } from 'react';
import { TableIdContext } from 'contexts/TableIdContext';
import { useFeatureFlag } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useExitToNextTable } from 'hooks/useExitToNextTable';
import { useTableSelector } from 'hooks/useTableSelector';
import { clearSitGoResult, SitGoResultEntry } from 'reducers/sitGoResult';
import useGameService from 'services/GameService';
import { markPlayerExitedTable } from 'utils/recentlyExitedTables';

import { landOnSitGoTab } from './sitGoLobbyLanding';

/**
 * This table's Sit & Go result entry (EN-11109), or null — always null with
 * release.sit-go-result-modal off, so every view of it renders nothing and the
 * table behaves exactly as before. Scoped to the panel's own table, and to the
 * tournament it holds, so a gameID reused by a later tournament never shows an
 * earlier result. A table whose tournament is not known yet (its slice has not
 * hydrated) shows nothing until it is, since it cannot be told apart from a
 * later tournament on the same gameID.
 */
export const useSitGoResultEntry = (): SitGoResultEntry | null => {
  const enabled = useFeatureFlag('sitGoResultModal');
  const gameID = useContext(TableIdContext);
  const tournamentId = useTableSelector((table) => table.game?.tournamentId);
  const entry = useAppSelector(
    (state) =>
      (state as any).sitGoResult?.byGameId?.[String(gameID)] as SitGoResultEntry | undefined
  );
  if (!enabled || !entry) return null;
  if (!tournamentId || String(tournamentId) !== entry.tournamentId) return null;
  return entry;
};

/**
 * Leave the finished Sit & Go the way the felt's Exit Table does and withdraw
 * the offer, then move on with `exitToNextTable`'s options. Does nothing
 * outside a table.
 *
 * The exit is recorded as out of the tournament: the player has a result, so
 * the engine dropping the table from GOT_PLAYER_GAMES and listing it again
 * must not bring it back (markPlayerExitedTable).
 */
const useLeaveFinishedSitGo = () => {
  const { leaveTable } = useGameService();
  const exitToNextTable = useExitToNextTable();
  const dispatch = useAppDispatch();
  const gameID = useContext(TableIdContext);

  return useCallback(
    (opts: Parameters<typeof exitToNextTable>[1]) => {
      const id = String(gameID || '');
      if (!id) return;
      if (leaveTable(true, true, true)) markPlayerExitedTable(id, { outOfTournament: true });
      dispatch(clearSitGoResult(id));
      exitToNextTable(id, opts);
    },
    [dispatch, exitToNextTable, gameID, leaveTable]
  );
};

/**
 * No: leave the finished Sit & Go and withdraw the offer, then hop to the
 * player's next open table — or, if this was their last, land on the lobby's
 * Sit & Go tab. Grid view keeps today's behaviour (the cell closes, the view
 * stays).
 */
export const useSitGoExit = () => {
  const leaveFinishedSitGo = useLeaveFinishedSitGo();

  return useCallback(
    (capacity: number) => leaveFinishedSitGo({ onReturnToLobby: () => landOnSitGoTab(capacity) }),
    [leaveFinishedSitGo]
  );
};

/**
 * Go to lobby: leave the finished Sit & Go the way No does, but always land on
 * the lobby's Sit & Go tab, never the player's next open table, in
 * single-table and grid view alike.
 *
 * The table is left because nothing holds it for the player: with no seat
 * locking yet (EN-11139 AC 10) the offer has no clock, so there is no running
 * rematch to come back to.
 */
export const useSitGoGoToLobby = () => {
  const leaveFinishedSitGo = useLeaveFinishedSitGo();

  return useCallback(
    (capacity: number) =>
      leaveFinishedSitGo({
        navigate: true,
        toLobby: true,
        onReturnToLobby: () => landOnSitGoTab(capacity)
      }),
    [leaveFinishedSitGo]
  );
};
